import { useCallback, useRef, useState } from 'react'
import { resolveParamsForUrl } from '@verzekeringapp-mono/shared/src/utils/base_params_utils'

type ExtraHeaders = Record<string, string | number | boolean | undefined>;

type OpenOptions = {
  token: string;
  headers?: ExtraHeaders;
  filename?: string;
  forceDownload?: boolean;
  extension?: string;
};

export function useSecureFile () {
  const [loading, setLoading] = useState(false)
  const urlsRef = useRef<string[]>([])

  const guessMimeFromExt = (ext?: string) => {
    const e = (ext || '').toLowerCase().replace(/^\./, '')

    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'heic', 'heif'].includes(e)) return 'image/*'
    if (['mp4', 'webm', 'mov', 'm4v'].includes(e)) return 'video/*'
    if (e === 'pdf') return 'application/pdf'
    if (['txt', 'log', 'md', 'csv'].includes(e)) return 'text/plain'

    return 'application/octet-stream'
  }

  const shouldOpenInline = (mime: string) =>
    mime.startsWith('image/') ||
    mime.startsWith('video/') ||
    mime === 'application/pdf' ||
    mime.startsWith('text/')

  const buildSafeFilename = (name?: string, ext?: string) => {
    const base =
      (name || 'file')
        .replace(/[/\\:*?"<>|]+/g, '_')
        .trim()
        .slice(0, 120) || 'file'

    return ext ? `${base}.${ext.replace(/^\./, '')}` : base
  }

  const getFilenameFromContentDisposition = (header: string | null) => {
    if (!header) return null
    const star = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i.exec(header)

    if (star?.[1]) {
      try {
        return decodeURIComponent(star[1])
      } catch {}
    }
    const quoted = /filename\s*=\s*"([^"]+)"/i.exec(header)

    if (quoted?.[1]) return quoted[1]
    const bare = /filename\s*=\s*([^;]+)/i.exec(header)

    if (bare?.[1]) return bare[1]

    return null
  }

  const triggerDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob)

    urlsRef.current.push(url)
    const a = document.createElement('a')

    a.href = url
    a.download = filename
    a.click()

    setTimeout(() => {
      URL.revokeObjectURL(url)
      urlsRef.current = urlsRef.current.filter((u) => u !== url)
    }, 10_000)
  }

  const fetchBlob = async (url: string, opts: OpenOptions) => {
    const { token, headers } = opts
    const auth = token.startsWith('Bearer ') ? token : `Bearer ${token}`

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: auth,
        ...(headers
          ? Object.fromEntries(
            Object.entries(headers).map(([k, v]) => [k, v === undefined ? '' : String(v)])
          )
          : {})
      }
    })

    if (!res.ok) {
      const text = await (async () => {
        try {
          return await res.text()
        } catch {
          return ''
        }
      })()

      throw new Error(`Download failed with status ${res.status}: ${text}`)
    }

    const blob = await res.blob()
    const cd = res.headers.get('Content-Disposition')
    const serverName = getFilenameFromContentDisposition(cd)

    return { blob, serverName }
  }

  const openFile = useCallback(
    async (url: string, opts: OpenOptions) => {
      setLoading(true)
      try {
        const { blob, serverName } = await fetchBlob(resolveParamsForUrl(url), opts)

        const extFromName =
          serverName?.split('.').pop() ||
          opts.extension?.replace(/^\./, '') ||
          undefined

        const mime = blob.type || guessMimeFromExt(extFromName)

        const filename =
          serverName ||
          buildSafeFilename(opts.filename || 'file', extFromName)

        if (opts.forceDownload || !shouldOpenInline(mime)) {
          triggerDownload(blob, filename)

          return
        }

        const objUrl = URL.createObjectURL(blob)

        urlsRef.current.push(objUrl)

        window.open(objUrl, '_blank', 'noopener,noreferrer')

        setTimeout(() => {
          URL.revokeObjectURL(objUrl)
          urlsRef.current = urlsRef.current.filter((u) => u !== objUrl)
        }, 30_000)
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const downloadFile = useCallback(
    async (url: string, opts: OpenOptions) => {
      setLoading(true)
      try {
        const { blob, serverName } = await fetchBlob(resolveParamsForUrl(url), opts)
        const extFromName =
          serverName?.split('.').pop() ||
          opts.extension?.replace(/^\./, '') ||
          undefined
        const filename =
          serverName ||
          buildSafeFilename(opts.filename || 'file', extFromName)

        triggerDownload(blob, filename)
      } finally {
        setLoading(false)
      }
    },
    []
  )

  return { openFile, downloadFile, loading }
}
